import { Button, Card, CardHeader, CardBody } from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

export function ChatbotError({
  handleSubmit,
  queryRef,
  isLoading,
  querySendError,
  chatbotResponseError,
  previousQuery,
}: {
  handleSubmit: (query?: string) => void;
  queryRef: React.RefObject<HTMLDivElement | null>;
  isLoading: boolean;
  querySendError: boolean;
  chatbotResponseError: boolean | undefined;
  previousQuery: string;
}) {
  const { t } = useTranslation('education');

  const getPreviousQuery = () => {
    const parser = new DOMParser();

    const doc = parser.parseFromString(previousQuery, 'text/html');
    const questionDiv = doc.querySelector('div.chatbot-question');
    const questionText = questionDiv?.textContent
      ? questionDiv.textContent.trim()
      : '';

    return questionText;
  };

  const previousQueryText = getPreviousQuery();

  useEffect(() => {
    const scrollWithError = () => {
      if (queryRef.current) {
        queryRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    };

    if ((chatbotResponseError || querySendError) && !isLoading) {
      scrollWithError();
    }
  }, [chatbotResponseError, querySendError, isLoading, queryRef]);

  return (
    <>
      {(chatbotResponseError || querySendError) && !isLoading && (
        <>
          <AnimatePresence mode="popLayout">
            <motion.div
              key="chatbot-error"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <Card
                shadow="none"
                className="self-start bg-red-100 max-w-[400px] border border-red-300 text-red-600 px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm"
              >
                <CardHeader className="gap-2 text-md items-center font-bold pb-0">
                  <Icon icon="mdi:warning-outline" className="text-2xl"></Icon>
                  <span>{t('chatboterror.title')}</span>
                </CardHeader>
                <CardBody className="text-md items-start gap-2">
                  <p>{t('chatboterror.text')}</p>
                  {previousQueryText !== '' ? (
                    <Button
                      className="px-6 bg-red-600 text-white font-bold"
                      onPress={() => handleSubmit(previousQueryText)}
                    >
                      {t('common.action.retry')}
                    </Button>
                  ) : (
                    <span className="text-red-600 font-bold">
                      {t('chatboterror.placeholder_text')}
                    </span>
                  )}
                </CardBody>
              </Card>
            </motion.div>
          </AnimatePresence>
        </>
      )}
    </>
  );
}
